"use client";
import type { GameData } from "@/app/games/(details)/types";
import { Action, ControlType } from "@/context/AnalyticsProvider";
import { useGameplayAnalytics } from "@/context/AnalyticsProvider/gameplay";
import { cn } from "@/lib/utils";
import Image from "next/image";
import { useEffect, useState } from "react";
import ReactPlayer from "react-player-v3";
import { FullScreenMedia } from "../FullScreenMedia/FullScreenMedia";
import { GamePageModal } from "../GamePageModal";

interface GameplayModalProps {
  gameData: GameData;
  isModalOpen: boolean;
  setIsModalOpen: (isModalOpen: boolean) => void;
}

export const GameplayModal = ({
  gameData,
  isModalOpen,
  setIsModalOpen,
}: GameplayModalProps) => {
  const { track } = useGameplayAnalytics();
  const [isMediaOpen, setIsMediaOpen] = useState(false);
  const [mediaIndex, setMediaIndex] = useState(-1);

  useEffect(() => {
    if (isModalOpen && gameData.details.game_id) {
      track({
        action: Action.VIEWED,
        extras: {
          id: "gameplay-modal",
          gameId: gameData.details.game_id,
          gameName: gameData.details.name,
          version: "v3",
        },
      });
    }
  }, [isModalOpen, gameData.details.game_id, gameData.details.name, track]);

  const handleMediaOpen = (index: number) => {
    if (gameData.details.game_id) {
      track({
        action: Action.PRESSED,
        extras: {
          id: "gameplay-media",
          control: "GameplayMedia",
          controlType: ControlType.IMAGE,
          gameId: gameData.details.game_id,
          gameName: gameData.details.name,
          imageIndex: index.toString(),
          version: "v3",
        },
      });
    }
    setMediaIndex(index);
    setIsMediaOpen(true);
  };

  const handleMediaHover = (index: number) => {
    if (gameData.details.game_id) {
      track({
        action: Action.HOVER,
        extras: {
          id: "gameplay-media-hover",
          control: "GameplayMediaHover",
          controlType: ControlType.IMAGE,
          gameId: gameData.details.game_id,
          gameName: gameData.details.name,
          imageIndex: index.toString(),
          version: "v3",
        },
      });
    }
    setMediaIndex(index);
  };

  const handleCloseMedia = () => {
    if (gameData.details.game_id) {
      track({
        action: Action.PRESSED,
        extras: {
          id: "gameplay-close-fullscreen",
          control: "ModalClose",
          controlType: ControlType.BUTTON,
          gameId: gameData.details.game_id,
          gameName: gameData.details.name,
          version: "v3",
        },
      });
    }
    setIsMediaOpen(false);
  };

  return (
    <>
      <GamePageModal
        title="Gameplay"
        gameTitle={gameData.details.name}
        gameIcon={gameData.details.logo_url}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        className="h-[95%] top-[52.5%] before:rounded-b-none [&>div]:rounded-b-none"
        gameId={gameData.details.game_id}
      >
        {/* Bento/Masonry Images Grid */}
        <div className="z-50 relative p-5 grid gap-5 overflow-y-auto auto-rows-[max-content] grid-cols-1 md:grid-cols-2 lg:grid-cols-[repeat(auto-fit,minmax(min(100%,380px),1fr))] md:auto-rows-[minmax(200px,240px)] lg:auto-rows-[minmax(220px,280px)] xl:auto-rows-[minmax(200px,320px)]">
          {gameData.gameContent?.gameplay_gallery_urls?.map(
            (mediaUrl, index) => (
              <button
                type="button"
                key={mediaUrl}
                className={cn(
                  "relative overflow-hidden rounded-lg group cursor-pointer",
                  index % 2 === 0 ? "md:row-span-2" : "md:row-span-1",
                )}
                onClick={() => handleMediaOpen(index)}
                onMouseEnter={() => handleMediaHover(index)}
                aria-label={`View ${gameData.details.name ?? "game"} gameplay ${index + 1}`}
              >
                {mediaUrl.endsWith(".webp") ? (
                  <Image
                    src={mediaUrl}
                    alt={`${gameData.details.name ?? "Game"} Gameplay ${index + 1}`}
                    fill
                    className="object-cover !relative transition-transform duration-300 ease-in-out group-hover:scale-105 size-full"
                    sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
                  />
                ) : (
                  <ReactPlayer
                    src={mediaUrl}
                    playing={mediaIndex === index}
                    playsInline
                    muted
                    loop
                    width="100%"
                    height="100%"
                    className="[--media-object-fit:cover] !relative transition-transform duration-300 ease-in-out group-hover:scale-105 size-full"
                  />
                )}
              </button>
            ),
          )}
        </div>
      </GamePageModal>

      <FullScreenMedia
        mediaURL={gameData.gameContent?.gameplay_gallery_urls?.[mediaIndex]}
        gameTitle={gameData.details.name}
        handleCloseMedia={handleCloseMedia}
        isMediaOpen={isMediaOpen}
        track={
          track as unknown as (event: {
            action: Action;
            extras: Record<string, unknown>;
          }) => void
        }
        gameId={gameData.details.game_id}
      />
    </>
  );
};
